import { createSelector } from 'redux-bundler';

import BootSchedule from '../models/boot_schedule';

export default {
  name: 'bootSchedules',

  getReducer: () => {
    const initialData = {
      data: null,
      loading: false,
      sortDirection: 'ASC',
      sortBy: 'group',
      activeBootSchedule: null,
      loadingActiveBootSchedule: false,
    };

    return (state = initialData, { type, payload }) => {
      if (type === 'FETCH_BOOT_SCHEDULES_START') {
        return { ...state, loading: true };
      }
      if (type === 'FETCH_BOOT_SCHEDULES_SUCCESS') {
        return { ...state, loading: false, data: payload.results };
      }
      if (type === 'FETCH_BOOT_SCHEDULE_START') {
        return { ...state, loadingActiveBootSchedule: true };
      }
      if (type === 'FETCH_BOOT_SCHEDULE_SUCCESS') {
        return {
          ...state,
          loadingActiveBootSchedule: false,
          activeBootSchedule: payload.result,
        };
      }
      if (type === 'CHANGE_BOOT_SCHEDULE_SORT') {
        return {
          ...state,
          sortDirection: payload.sortDirection,
          sortBy: payload.sortBy,
        };
      }
      if (type === 'ADD_BOOT_SCHEDULE' && state.data) {
        return { ...state, data: [...state.data, payload.bootSchedule] };
      }
      if (type === 'UPDATE_BOOT_SCHEDULE') {
        return {
          ...state,
          activeBootSchedule: payload.bootSchedule,
          data: state.data
            ? state.data.map(s =>
                s.id === payload.bootSchedule.id ? payload.bootSchedule : s,
              )
            : null,
        };
      }
      if (type === 'REMOVE_BOOT_SCHEDULE') {
        return {
          ...state,
          data: state.data
            ? state.data.filter(s => s.id !== payload.bootSchedule.id)
            : null,
        };
      }
      if (type === 'RESET_ACTIVE_BOOT_SCHEDULE') {
        return { ...state, activeBootSchedule: null };
      }
      if (type === 'RESET_BOOT_SCHEDULES') {
        return { ...state, data: null };
      }
      if (type === 'RESET_ACTIVE_FACILITY') {
        return { ...state, data: null };
      }
      return state;
    };
  },

  doFetchBootSchedules:
    () =>
    async ({ dispatch, getState, store }) => {
      const state = getState();
      const authenticatedUser = store.selectAuthenticatedUser(state);
      if (
        !authenticatedUser ||
        !authenticatedUser.isAuthorized('boot.schedule.index')
      ) {
        return;
      }
      const facility = store.selectActiveFacility(state);

      dispatch({ type: 'FETCH_BOOT_SCHEDULES_START' });
      const response = await BootSchedule.where({ facility_id: facility.id })
        .includes(['group'])
        .all();
      dispatch({
        type: 'FETCH_BOOT_SCHEDULES_SUCCESS',
        payload: { results: response.data },
      });
    },

  doFetchActiveBootSchedule:
    bootScheduleId =>
    async ({ dispatch, getState, store }) => {
      const authenticatedUser = store.selectAuthenticatedUser(getState());
      if (
        !authenticatedUser ||
        !authenticatedUser.isAuthorized('boot.schedule.show')
      ) {
        return;
      }
      dispatch({ type: 'FETCH_BOOT_SCHEDULE_START' });
      const response = await BootSchedule.includes(['group']).find(
        bootScheduleId,
      );
      dispatch({
        type: 'FETCH_BOOT_SCHEDULE_SUCCESS',
        payload: { result: response.data },
      });
    },

  doChangeBootScheduleSort:
    ({ sortDirection, sortBy }) =>
    ({ dispatch }) => {
      dispatch({
        type: 'CHANGE_BOOT_SCHEDULE_SORT',
        payload: { sortDirection, sortBy },
      });
    },

  doCreateBootSchedule:
    values =>
    async ({ dispatch, getState, store }) => {
      const state = getState();
      const authenticatedUser = store.selectAuthenticatedUser(state);
      if (
        !authenticatedUser ||
        !authenticatedUser.isAuthorized('boot.schedule.create')
      ) {
        return null;
      }
      const facility = store.selectActiveFacility(state);

      const bootSchedule = new BootSchedule({
        facility,
        ...values,
      });

      await bootSchedule.save({ with: ['facility.id'] });

      if (bootSchedule.isPersisted) {
        store.doFetchBootSchedules();
      }
      return bootSchedule;
    },

  doUpdateBootSchedule:
    values =>
    async ({ dispatch, getState, store }) => {
      const state = getState();
      const authenticatedUser = store.selectAuthenticatedUser(state);
      if (
        !authenticatedUser ||
        !authenticatedUser.isAuthorized('boot.schedule.update')
      ) {
        return null;
      }
      const activeBootSchedule = store.selectActiveBootSchedule(state);
      const bootSchedule = activeBootSchedule.dup();

      bootSchedule.assignAttributes(values);
      await bootSchedule.save();

      if (bootSchedule.isPersisted) {
        const response = await BootSchedule.includes(['group']).find(
          bootSchedule.id,
        );
        dispatch({
          type: 'UPDATE_BOOT_SCHEDULE',
          payload: { bootSchedule: response.data },
        });
        return response.data;
      }
      return bootSchedule;
    },

  doToggleBootScheduleEnabled:
    bootSchedule =>
    async ({ dispatch, getState, store }) => {
      const authenticatedUser = store.selectAuthenticatedUser(getState());
      if (
        !authenticatedUser ||
        !authenticatedUser.isAuthorized('boot.schedule.update')
      ) {
        return null;
      }
      const next = bootSchedule.dup();
      next.enabled = !bootSchedule.enabled;
      await next.save();
      if (next.isPersisted) {
        dispatch({
          type: 'UPDATE_BOOT_SCHEDULE',
          payload: { bootSchedule: next },
        });
      }
      return next;
    },

  doDestroyBootSchedule:
    bootSchedule =>
    async ({ dispatch, getState, store }) => {
      const authenticatedUser = store.selectAuthenticatedUser(getState());
      if (
        !authenticatedUser ||
        !authenticatedUser.isAuthorized('boot.schedule.destroy')
      ) {
        return;
      }
      const next = bootSchedule.dup();
      await next.destroy();
      dispatch({
        type: 'REMOVE_BOOT_SCHEDULE',
        payload: { bootSchedule: next },
      });
    },

  doResetActiveBootSchedule:
    () =>
    ({ dispatch }) => {
      dispatch({ type: 'RESET_ACTIVE_BOOT_SCHEDULE' });
    },

  selectBootScheduleRaw: state => state.bootSchedules,
  selectBootScheduleData: createSelector(
    'selectBootScheduleRaw',
    raw => {
      const data = raw.data || [];
      if (!raw.sortBy) return data;
      const dir = raw.sortDirection === 'DESC' ? -1 : 1;
      const getValue = (item, key) => {
        if (key === 'group') return item.group ? item.group.name : '';
        return item[key];
      };
      const compare = (a, b, key) => {
        const av = getValue(a, key);
        const bv = getValue(b, key);
        if (av == null && bv == null) return 0;
        if (av == null) return 1;
        if (bv == null) return -1;
        if (typeof av === 'string') return av.localeCompare(bv);
        if (av < bv) return -1;
        if (av > bv) return 1;
        return 0;
      };
      return [...data].sort((a, b) => {
        const primary = compare(a, b, raw.sortBy);
        if (primary !== 0) return dir * primary;
        // Secondary: when sorting by group, order by time ascending
        if (raw.sortBy === 'group') return compare(a, b, 'time');
        return 0;
      });
    },
  ),
  selectBootScheduleLoading: state => state.bootSchedules.loading,
  selectBootScheduleSortBy: state => state.bootSchedules.sortBy,
  selectBootScheduleSortDirection: state => state.bootSchedules.sortDirection,

  selectActiveBootScheduleId: createSelector(
    'selectRouteParams',
    routeParams => routeParams.bootScheduleId,
  ),

  selectActiveBootSchedule: createSelector(
    'selectBootScheduleRaw',
    raw => raw.activeBootSchedule,
  ),

  reactShouldFetchBootSchedules: createSelector(
    'selectRouteApis',
    'selectBootScheduleRaw',
    'selectCurrentUser',
    'selectActiveFacility',
    (apis, raw, currentUser, activeFacility) => {
      const wants = apis.includes('boot_schedules');
      if (!wants || raw.loading || raw.data || !currentUser || !activeFacility) {
        return false;
      }
      return { actionCreator: 'doFetchBootSchedules' };
    },
  ),

  reactShouldFetchActiveBootSchedule: createSelector(
    'selectRouteParams',
    'selectPathname',
    'selectActiveBootSchedule',
    'selectBootScheduleRaw',
    (routeParams, pathname, activeBootSchedule, raw) => {
      if (
        !pathname.includes('/boot_schedules') ||
        !routeParams.bootScheduleId ||
        raw.loadingActiveBootSchedule
      ) {
        return null;
      }
      if (
        activeBootSchedule &&
        activeBootSchedule.id === routeParams.bootScheduleId
      ) {
        return null;
      }
      return {
        actionCreator: 'doFetchActiveBootSchedule',
        args: [routeParams.bootScheduleId],
      };
    },
  ),

  reactShouldResetActiveBootSchedule: createSelector(
    'selectActiveBootScheduleId',
    'selectActiveBootSchedule',
    (activeId, activeBootSchedule) => {
      if (!activeBootSchedule) return null;
      if (activeBootSchedule.id !== activeId) {
        return { actionCreator: 'doResetActiveBootSchedule' };
      }
      return null;
    },
  ),
};
